import { useParams } from '@reach/router';
import { graphql } from 'gatsby';
import queryString from 'query-string';
import React, { FC, useMemo } from 'react';

import { useGetPlayerOverviewPageLink } from '@/bits/links/useLink';
import {
  Button,
  Card,
  CardBody,
  CardCloseButton,
  CardOptions,
  CardOptionsButton,
  ControlledModal,
  Date as DateComponent,
  InlineIconButton,
  Value,
} from '@/components';
import { useDataGrid } from '@/components/DataGrid';
import {
  DuplicateIcon,
  ExternalLinkIcon,
  InformationIcon,
  NextIcon,
  PlusIcon,
  PreviousIcon,
  RefreshIcon,
  RemoveIcon,
} from '@/components/icons';
import MultipleValues from '@/components/MultipleValues';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@/components/Tabs';
import { useTranslate } from '@/contexts';
import { ChallengePlayerAssignForm, ChallengePlayerRemoveForm } from '@/forms';
import { ChallengeCriteria, ChallengeStatus } from '@/globalTypes';
import ChallengeCloneForm from './ChallengeCloneForm';
import { renderRewardDetails } from './renderRewardDetails';
import useChallengeDetail from './useChallengeDetail';

export const Fragment = graphql`
  fragment SanityChallengeDetailBlock on SanityChallengeDetailBlock {
    challengeId {
      ...SanityLocaleString
    }
    challengeUuid {
      ...SanityLocaleString
    }
    name {
      ...SanityLocaleString
    }
    status {
      ...SanityLocaleString
    }
    startDate {
      ...SanityLocaleString
    }
    endDate {
      ...SanityLocaleString
    }
    countries {
      ...SanityLocaleString
    }
    brands {
      ...SanityLocaleString
    }
    eligibleCriteria {
      ...SanityLocaleString
    }
    challengeActions {
      ...SanityLocaleString
    }
    challengeRewards {
      ...SanityLocaleString
    }
    infoTab {
      ...SanityLocaleString
    }
    playersTab {
      ...SanityLocaleString
    }
    cardTitle {
      ...SanityLocaleString
    }
    numberOfPlayers {
      ...SanityLocaleString
    }
    updatedAt {
      ...SanityLocaleString
    }
    agent {
      ...SanityLocaleString
    }
    reason {
      ...SanityLocaleString
    }
    actions {
      ...SanityLocaleString
    }
    statusCompleted {
      ...SanityLocaleString
    }
    statusActive {
      ...SanityLocaleString
    }
  }
`;

const URLZ_CHALLENGE_URLS_BASE =
  'https://urlz.ly/dino-backoffice/challenge-urls';

const ChallengeDetailBlock: FC<{
  block: Queries.SanityChallengeDetailBlockFragment;
}> = ({ block }) => {
  const { t } = useTranslate();
  const params = useParams();

  const {
    desc,
    setOrderBy,
    challenge,
    fetching,
    defaultFilter,
    updateFilter,
    players,
    nextPage,
    previousPage,
    refresh,
  } = useChallengeDetail(params.challengeId);

  const getPlayerOverviewPageLink = useGetPlayerOverviewPageLink();

  const { DataGrid, GlobalCardOptions } = useDataGrid({
    name: 'challengeDetail',
    data: players,
    fetching,
    setOrderBy,
    desc,
    defaultFilter,
    updateFilter,
    schema: (s) => [
      s.stringValue({
        field: 'playerUuid',
        title: 'Player UUID',
        getValue: ({ row }) => row.playerUUID,
        linkTo: ({ row }) => getPlayerOverviewPageLink(row.playerId) || '',
      }),
      s.stringValue({
        field: 'timesCompleted',
        title: 'Completed (# of times)',
      }),
      s.stringValue({
        field: 'timesRestarted',
        title: 'Restarted (# of times)',
      }),
      s.stringValue({
        field: 'status',
        title: 'Status',
      }),
    ],
  });

  const tabIndex =
    Number(new URLSearchParams(window.location.search).get('tabIndex')) || 0;

  const urlzChallengeUrl = useMemo(() => {
    const challengeId = challenge?.challengeUUID;

    if (!challengeId) {
      return null;
    }

    const brands =
      challenge?.brands
        ?.filter((brand) => Boolean(brand))
        .sort((a, b) => a.localeCompare(b)) ?? [];

    const query = queryString.stringify(
      {
        challengeId,
        ...(brands.length > 0 ? { brands: brands.join(',') } : {}),
      },
      { encode: false },
    );

    return `${URLZ_CHALLENGE_URLS_BASE}?${query}`;
  }, [challenge?.brands, challenge?.challengeUUID]);

  const challengeRewards = useMemo(
    () => challenge?.challengeRewards ?? [],
    [challenge?.challengeRewards],
  );

  const challengeRewardsNames = useMemo(
    () =>
      challengeRewards?.map(
        (reward) => reward.challengeRewardType?.name || 'N/A',
      ),
    [challengeRewards],
  );

  const paymentIntegration = useMemo(() => {
    const values =
      challenge?.challengeCriteria?.reduce<string[]>((acc, criteria) => {
        if (
          criteria.__typename === 'DepositChallengeCriteria' &&
          criteria.paymentIntegration
        ) {
          acc.push(criteria.paymentIntegration);
        }

        return acc;
      }, []) ?? [];

    return Array.from(new Set(values)).join(', ');
  }, [challenge?.challengeCriteria]);

  const hasDepositCriteria =
    challenge?.challengeActions?.includes(ChallengeCriteria.Deposit) || false;

  const isChallengeActive =
    challenge?.status === ChallengeStatus.Activated ||
    challenge?.status === ChallengeStatus.Scheduled;

  const hasNoAssignedPlayers = challenge?.numberOfPlayers === 0;

  return (
    <Card
      size="lg"
      title={`${t(block.cardTitle)} - ${challenge?.name}`}
      showOptionsAtBottom
      headerOptions={
        challenge ? (
          <CardOptions>
            <ControlledModal
              content={<ChallengeCloneForm challenge={challenge} />}
            >
              <CardOptionsButton tippyContent="Clone challenge">
                <DuplicateIcon />
              </CardOptionsButton>
            </ControlledModal>
          </CardOptions>
        ) : null
      }
    >
      <CardBody>
        <Tabs defaultIndex={tabIndex}>
          <TabList>
            <Tab>{t(block.infoTab)}</Tab>
            <Tab>{t(block.playersTab)}</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <div className="p-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                <Value
                  fetching={fetching}
                  title={t(block.challengeUuid)}
                  value={challenge?.challengeUUID}
                />
                <Value
                  fetching={fetching}
                  title={t(block.name)}
                  value={challenge?.name}
                />
                <Value
                  fetching={fetching}
                  title={t(block.numberOfPlayers)}
                  value={challenge?.numberOfPlayers}
                />
                <Value
                  fetching={fetching}
                  title={t(block.status)}
                  value={challenge?.status}
                />
                <MultipleValues
                  fetching={fetching}
                  title={t(block.brands)}
                  rows={challenge?.brands?.map((brand) => ({
                    label: 'Brand Code',
                    value: brand,
                  }))}
                />
                <MultipleValues
                  fetching={fetching}
                  title={t(block.countries)}
                  rows={challenge?.countries?.map((country) => ({
                    label: 'Country Code',
                    value: country,
                  }))}
                />
                <Value
                  fetching={fetching}
                  title={t(block.startDate)}
                  value={challenge?.startDate}
                >
                  <DateComponent date={challenge?.startDate} />
                </Value>
                <Value
                  fetching={fetching}
                  title={t(block.endDate)}
                  value={challenge?.endDate}
                >
                  <DateComponent date={challenge?.endDate} />
                </Value>
                <Value fetching={fetching} title="Rewards">
                  {challengeRewardsNames?.join(', ')}
                  <ControlledModal
                    content={
                      <Card
                        size="lg"
                        title="Rewards"
                        options={
                          <CardOptions>
                            <CardCloseButton />
                          </CardOptions>
                        }
                      >
                        {challengeRewards.map((reward, index) => (
                          <div className="p-6" key={index}>
                            {renderRewardDetails(reward)}
                          </div>
                        ))}
                      </Card>
                    }
                  >
                    <InlineIconButton>
                      <InformationIcon />
                    </InlineIconButton>
                  </ControlledModal>
                </Value>
                <Value fetching={fetching} title="Short link">
                  {urlzChallengeUrl ? (
                    <Button
                      as="a"
                      variant="outlined"
                      href={urlzChallengeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 py-1 px-2"
                    >
                      Create short link
                      <ExternalLinkIcon />
                    </Button>
                  ) : (
                    '-'
                  )}
                </Value>
                {hasDepositCriteria && (
                  <Value fetching={fetching} title="Payment Integration">
                    {paymentIntegration || '-'}
                  </Value>
                )}
              </div>
            </TabPanel>
            <TabPanel>
              <Card
                size="lg"
                title={t(block.playersTab)}
                showOptionsAtBottom
                options={
                  <CardOptions>
                    <CardOptionsButton
                      disabled={!previousPage}
                      onClick={() => previousPage && previousPage()}
                    >
                      <PreviousIcon />
                    </CardOptionsButton>
                    <CardOptionsButton
                      disabled={!nextPage}
                      onClick={() => nextPage && nextPage()}
                    >
                      <NextIcon />
                    </CardOptionsButton>
                    <GlobalCardOptions />
                    <CardOptionsButton
                      disabled={!isChallengeActive}
                      tippyContent="Refresh"
                      onClick={() => refresh({ requestPolicy: 'network-only' })}
                    >
                      <RefreshIcon />
                    </CardOptionsButton>
                    <ControlledModal
                      content={
                        <ChallengePlayerAssignForm
                          challengeId={challenge?.challengeUUID}
                          challengeName={challenge?.name}
                        />
                      }
                    >
                      <CardOptionsButton
                        disabled={!isChallengeActive}
                        tippyContent="Assign Player(s)"
                      >
                        <PlusIcon />
                      </CardOptionsButton>
                    </ControlledModal>
                    <ControlledModal
                      content={
                        <ChallengePlayerRemoveForm
                          challengeId={challenge?.challengeUUID}
                          challengeName={challenge?.name}
                        />
                      }
                    >
                      <CardOptionsButton
                        disabled={!isChallengeActive || hasNoAssignedPlayers}
                        tippyContent="Remove Player(s)"
                      >
                        <RemoveIcon />
                      </CardOptionsButton>
                    </ControlledModal>
                  </CardOptions>
                }
              >
                <CardBody>
                  <DataGrid />
                </CardBody>
              </Card>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </CardBody>
    </Card>
  );
};

export default ChallengeDetailBlock;
